import React, { memo, useEffect, useState, useRef } from "react";
import Select, { components } from "react-select";
import chroma from "chroma-js";
import { useDispatch, useSelector } from "react-redux";
import { selectLabel } from "redux/points/actions";
import { selectPermissionComponent } from "redux/app/selectors";
import { selectLabels, selectCurrLabel } from "redux/points/selectors";
import { mapModeToIcon } from "components/ShapeList/icon";
import { getParameterByName } from "helpers";
import { selectIsUserTypingStatus } from "redux/keyboard/selectors";
import { setUserTypingStatus } from "redux/keyboard/action";
import { useShortcutKeyBoard } from "hooks/useShortcutKeyBoard";
import { normalizeKeys, formatShortcut } from "./shortcutKeys";
import { dot } from "./stylesSelect";
import "./style.scss";

const CustomSelectInput = (props) => {
  const customProps = Object.assign({}, props);
  delete customProps.autoCorrect;
  delete customProps.autoCapitalize;
  return <components.Input {...customProps} />;
};

function LabelPicker() {
  const labels = useSelector(selectLabels).map((label) => ({
    label: label.name,
    value: label.id,
    id: label.id,
    color: label.color,
    key: label.id,
    tools: label.tools,
    shortcutKey: label.shortcutKey,
    shortcutKeyArray: label.shortcutKeyArray,
  }));
  const modifyPermission = useSelector(
    selectPermissionComponent("labeling_modify")
  );
  useShortcutKeyBoard(labels);
  const [displayOptions, setDisplayOptions] = useState([]);
  const isActionHistory = getParameterByName("isActionHistory");
  const isViewResult = getParameterByName("isViewResult");
  const isBenchmark = isViewResult || isActionHistory;
  const isUserTyping = useSelector(selectIsUserTypingStatus);
  const dispatch = useDispatch();
  const measureRef = useRef(null);
  const onFocus = () => {
    if (!isUserTyping) dispatch(setUserTypingStatus(true));
  };
  const onBlur = () => {
    dispatch(setUserTypingStatus(false));
  };
  const currentLabel = useSelector(selectCurrLabel);

  function measureTextWidth(text, font = "16px Nunito") {
    const canvas =
      measureTextWidth.canvas ||
      (measureTextWidth.canvas = document.createElement("canvas"));
    const context = canvas.getContext("2d");
    context.font = font;

    const metrics = context.measureText(text);

    return metrics.width + 100;
  }

  useEffect(() => {
    if (labels?.length > 0) {
      const boxOptions = labels.filter((label) =>
        JSON.stringify(label.tools).includes("box")
      );
      const selectedOption = isBenchmark
        ? labels.find((label) => JSON.stringify(label.tools).includes("box"))
        : labels[0];
      dispatch(selectLabel(selectedOption));
      if (isBenchmark) {
        setDisplayOptions(boxOptions);
      } else {
        const sortedData = [...labels].sort((a, b) =>
          a.label.localeCompare(b.label, "vi", { sensitivity: "base" })
        );
        setDisplayOptions(sortedData);
      }
    }
  }, [labels?.length]);

  function handleChange(selectedOptionValue) {
    dispatch(selectLabel(selectedOptionValue));
    measureRef.current = measureTextWidth(
      `${selectedOptionValue.label} ${
        selectedOptionValue.shortcutKey &&
        `(${selectedOptionValue.shortcutKey})`
      }`
    );
  }

  const formatOptionLabel = (data) => {
    const { label, tools, color, shortcutKeyArray } = data;
    const Icon = tools && mapModeToIcon && mapModeToIcon[tools[0]];
    return (
      <div style={{ display: "flex" }}>
        <div style={{ marginRight: "10px", color: "#ccc" }}>
          {Icon && (
            <Icon
              color={"inherit"}
              stroke={color}
              style={{ width: 20, height: 20 }}
            />
          )}
        </div>
        <div style={{ fontSize: 15, fontWeight: 600 }}>
          {label}{" "}
          {shortcutKeyArray.length > 0 &&
            `(${formatShortcut(normalizeKeys(shortcutKeyArray))})`}
        </div>
      </div>
    );
  };

  const colorStyles = {
    control: (styles) => ({
      ...styles,
      border: "none",
      minWidth: measureRef.current ?? "275px",
      cursor: "pointer",
    }),
    menu: (styles) => ({
      ...styles,
      minWidth: "200px",
    }),
    input: (styles) => ({ ...styles, ...dot() }),
    placeholder: (styles) => ({
      ...styles,
      ...dot(),
      fontSize: "15px",
      fontWeight: 600,
      color: "#000",
    }),
    singleValue: (styles, { data }) => ({
      ...styles,
      ...dot(data.color),
      fontSize: "15px",
      fontWeight: 600,
    }),
    option: (styles, { data, isDisabled, isSelected }, provided) => {
      const color = chroma.valid(data.color) ? chroma(data.color) : data.color;
      return {
        ...styles,
        color: isDisabled ? "#ccc" : isSelected ? "white" : "black",
        cursor: isDisabled ? "not-allowed" : "default",

        ":active": {
          ...styles[":active"],
          backgroundColor:
            !isDisabled && (isSelected ? data.color : color.alpha(0.3).css()),
        },
        ...provided,
        paddingBottom: "10px",
        paddingLeft: "10px",
        height: "50px",
        overflow: "hidden",
      };
    },
  };

  return (
    <>
      <span className="label">Choose Class</span>
      <div data-tour="choose-class-input">
        <Select
          placeholder="Choose a class"
          classNamePrefix="container"
          components={{ Input: CustomSelectInput }}
          name="form-field-name"
          value={currentLabel}
          onChange={handleChange}
          options={displayOptions}
          styles={colorStyles}
          defaultValue={labels[0]}
          blurInputOnSelect
          isDisabled={!modifyPermission}
          formatOptionLabel={formatOptionLabel}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </div>
    </>
  );
}

export default memo(LabelPicker);
